import { Button, Input, message, Modal, Switch } from "antd";
import React, { useEffect } from "react";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Bank,
  useCreateBank,
  useUpdateBank,
} from "../../api/banks";

const bankSchema = z.object({
  bankName: z
    .string()
    .min(1, { message: "Bank name is required" })
    .max(255, { message: "Bank name is too long" }),
  code: z
    .string()
    .max(50, { message: "Code is too long" })
    .optional()
    .or(z.literal("")),
  swiftCode: z
    .string()
    .max(50, { message: "SWIFT code is too long" })
    .optional()
    .or(z.literal("")),
  country: z
    .string()
    .max(100, { message: "Country is too long" })
    .optional()
    .or(z.literal("")),
  isActive: z.boolean(),
});

type BankForm = z.infer<typeof bankSchema>;

type BankModalProps = {
  open: boolean;
  /** Pre-fetched bank to edit; pass `undefined` for create. */
  selectedBank?: Bank | null;
  hideModal: () => void;
};

const blankForm: BankForm = {
  bankName: "",
  code: "",
  swiftCode: "",
  country: "",
  isActive: true,
};

const BankModal: React.FC<BankModalProps> = ({
  open,
  selectedBank,
  hideModal,
}) => {
  const { mutateAsync: createBank, isPending: creating } = useCreateBank();
  const { mutateAsync: updateBank, isPending: updating } = useUpdateBank();

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<BankForm>({
    resolver: zodResolver(bankSchema) as any,
    defaultValues: blankForm,
    mode: "onChange",
  });

  useEffect(() => {
    if (!open) return;
    if (selectedBank) {
      reset({
        bankName: selectedBank.bankName ?? "",
        code: selectedBank.code ?? "",
        swiftCode: selectedBank.swiftCode ?? "",
        country: selectedBank.country ?? "",
        isActive: selectedBank.isActive ?? true,
      });
    } else {
      reset(blankForm);
    }
  }, [open, selectedBank, reset]);

  const onSubmit: SubmitHandler<BankForm> = async (form) => {
    const payload = {
      bankName: form.bankName.trim(),
      code: form.code?.trim() || null,
      swiftCode: form.swiftCode?.trim() || null,
      country: form.country?.trim() || null,
      isActive: form.isActive,
    };

    try {
      if (selectedBank?.id) {
        await updateBank({ id: selectedBank.id, payload });
        message.success("Bank updated successfully");
      } else {
        await createBank(payload);
        message.success("Bank created successfully");
      }
      hideModal();
    } catch (err: any) {
      const apiMessage =
        err?.response?.data?.message ||
        err?.response?.data ||
        err?.message ||
        "Operation failed";
      message.error(
        typeof apiMessage === "string" ? apiMessage : "Operation failed",
      );
    }
  };

  return (
    <Modal
      destroyOnClose
      open={open}
      centered
      onCancel={hideModal}
      title={
        <div className="flex flex-col items-center justify-center font-semibold text-black text-[22px]">
          {selectedBank ? "Edit Bank" : "Create Bank"}
        </div>
      }
      footer={
        <div className="flex justify-end gap-3 p-4">
          <Button
            onClick={hideModal}
            className="!border-gray-300 !text-gray-700 font-medium hover:!bg-gray-100 hover:!text-gray-900 transition-all duration-200 bg-transparent"
          >
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleSubmit(onSubmit)}
            className="!bg-[#009602] !border-none hover:!bg-[#00b306] font-medium transition-all duration-200"
            loading={creating || updating}
            disabled={creating || updating}
          >
            {selectedBank ? "Update" : "Create"}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Bank name *
          </label>
          <Controller
            name="bankName"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                placeholder="e.g. Commercial Bank of Ethiopia"
                status={errors.bankName ? "error" : undefined}
              />
            )}
          />
          {errors.bankName && (
            <span className="text-red-600 text-xs">
              {errors.bankName.message}
            </span>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Code
            </label>
            <Controller
              name="code"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  placeholder="e.g. CBE"
                  status={errors.code ? "error" : undefined}
                />
              )}
            />
            {errors.code && (
              <span className="text-red-600 text-xs">
                {errors.code.message}
              </span>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              SWIFT code
            </label>
            <Controller
              name="swiftCode"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  placeholder="e.g. CBETETAA"
                  status={errors.swiftCode ? "error" : undefined}
                />
              )}
            />
            {errors.swiftCode && (
              <span className="text-red-600 text-xs">
                {errors.swiftCode.message}
              </span>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Country
          </label>
          <Controller
            name="country"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                placeholder="e.g. Ethiopia"
                status={errors.country ? "error" : undefined}
              />
            )}
          />
          {errors.country && (
            <span className="text-red-600 text-xs">
              {errors.country.message}
            </span>
          )}
        </div>

        <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg bg-gray-50">
          <span className="font-medium text-gray-700">Active</span>
          <Controller
            name="isActive"
            control={control}
            render={({ field }) => (
              <Switch
                checked={!!field.value}
                onChange={(checked) => field.onChange(checked)}
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                className={field.value ? "!bg-green-600" : "!bg-gray-400"}
              />
            )}
          />
        </div>
      </div>
    </Modal>
  );
};

export default BankModal;
